
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import { Component, InjectReactive, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import TitleRankingList from '@/components/TitleRankingList.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import TitleCreditName from '@/components/details/TitleCreditName.vue';
const ProvideStreamingChartData = () => import('@/components/streaming-charts/ProvideStreamingChartData.vue');
import TitleDetailStreamingChart from '@/components/details/streaming-chart/TitleDetailStreamingChart.vue';

import { SnowplowContext } from '@/helpers/tracking/providers';

import { CreditRole, ObjectType } from '@/@types/graphql-types';
import { HtmlContentMovieDetails, UrlMetadataResponse } from '@/interfaces/responses/url-metadata';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';

import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';

import { GenresByShortNameConstant } from '@/constants/ConstantsProvider.vue';
import type { Genres } from '@/constants/types';
import { formatRuntime } from '@/helpers/title-helper';

const Routing = namespace('routing');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'TitleInfo',
	components: {
		TitleScoreList,
		TitleCreditName,
		TitleRankingList,
		ProvideStreamingChartData,
		TitleDetailStreamingChart,
	},
})
export default class TitleInfo extends Vue {
	@Prop({ required: true }) title: TitleDetailsNode;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@InjectReactive(GenresByShortNameConstant) genresByShortName: Genres;

	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.Getter('urlMetadata') contentData: UrlMetadataResponse<HtmlContentMovieDetails>;

	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;

	get titleContent() {
		return this.title.content;
	}

	get jwVotes() {
		if (this.title.content.interactions?.votesNumber) return this.title.content.interactions.votesNumber;
		else return null;
	}

	get directors() {
		const credits =
			(this.title.objectType === ObjectType.ShowSeason
				? this.title.show?.content.credits
				: this.titleContent.credits) ?? [];

		return credits.filter(credit => credit.role === CreditRole.Director);
	}

	get runtimeLabel(): TranslateResult {
		return this.$t('WEBAPP_RUNTIME');
	}

	get runtime(): string {
		const runtimeValue = this.titleContent.runtime!;
		const { translationKey, minutes, hours } = formatRuntime(runtimeValue);

		return this.$t(translationKey, { minutes, hours }) as string;
	}

	get htmlContent() {
		return (this.contentData && this.contentData.html_content) || {};
	}

	get productionCountries() {
		return this.titleContent.productionCountries?.map(country => this.$t(`WEBAPP_COUNTRY_${country}`)).join(', ');
	}

	get genres() {
		return this.titleContent.genres
			.map(genre => this.genresByShortName[genre.shortName]?.translation)
			.filter(translation => !!translation)
			.join(', ');
	}

	get scoring() {
		return this.titleContent.scoring;
	}

	get isReleased() {
		return this.titleContent.isReleased;
	}

	get isStreamingChartCountry() {
		return streamingChartCountries.includes(this.country);
	}
}
